import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Typography,
  Button,
  Grid,
  MenuItem,
  ListItemText,
} from '@mui/material';

import TextField from '../text-field/TextField';
import MultiSelect from '../multi-select/MultiSelect';
import Checkbox from '../checkbox/Checkbox';

import {
  useGetAuthenticationQuery,
  useCreateLocalDomainMutation,
  useGetDomainControllersQuery,
} from '../../services/ADapi';

import { validateLocalDomainName } from '../../utils/validateLocalDomainName';

import { notificationSuccess } from '../../store/notificationReducer';

const MenuProps = {
  PaperProps: {
    style: {
      width: 262,
      padding: 0,
    },
  },
};

const LocalDomainsForm = () => {
  const dispatch = useDispatch();
  const [localDomainName, setLocalDomainName] = useState('');
  const [domainController, setDomainController] = useState([]);
  const [isLocalDomainsFormValid, setLocalDomainsFormState] = useState(false);
  const [errors, setErrors] = useState({});

  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: controllers } = useGetDomainControllersQuery(user_id);
  const [createLocalDomain] = useCreateLocalDomainMutation();

  // TODO: Here we definitely need to wait until all entities are loaded
  useEffect(() => {
    setDomainController([controllers?.[0]]);
  }, [controllers]);

  useEffect(() => {
    const localDomainNameValid = validateLocalDomainName(localDomainName);

    setLocalDomainsFormState(
      localDomainNameValid && domainController.length > 0
    );
  }, [localDomainName, domainController]);

  const localDomainNameHandler = (e) => {
    const errorsCopy = { ...errors };
    delete errorsCopy.domain;
    setErrors(errorsCopy);
    setLocalDomainName(e.target.value);
  };

  const handleChange = (e) => {
    const value = e.target.value;

    const errorsCopy = { ...errors };
    delete errorsCopy.controller_ids;

    setErrors(errorsCopy);

    setLocalDomainsFormState(true);

    setDomainController(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    );
  };

  const createLocalDomainHandler = async () => {
    const ids = domainController.map((c) => c.id);

    try {
      await createLocalDomain({
        user_id,
        localDomain: {
          domain: localDomainName,
          controller_ids: ids,
        },
      }).unwrap();

      setLocalDomainName('');
      setDomainController([controllers[0]]);

      dispatch(
        notificationSuccess({
          isNotification: true,
          type: 'success',
          message: 'Local Domain created successfully',
        })
      );
    } catch (error) {
      setErrors(error);
      setLocalDomainsFormState(false);
    }
  };

  return (
    <>
      <Typography variant="h1" component="h2">
        Local Domains
      </Typography>
      <Grid container spacing={3} sx={{ alignItems: 'flex-end' }}>
        <Grid item xs={4}>
          <TextField
            id="localDomainName"
            label="Create local domain"
            placeholder="Name"
            value={localDomainName}
            onChange={localDomainNameHandler}
            size="small"
          />
          {errors.domain ? (
            <Typography variant="body2" color="#e08d8d" sx={{ mb: '-20px' }}>
              {errors.domain}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={4}>
          <MultiSelect
            id="selectDomainController"
            name="select-domain-controller"
            label="Choose Domain Controllers"
            multiple
            value={domainController}
            onChange={handleChange}
            renderValue={(selected) =>
              selected
                .map(
                  (val) =>
                    // TODO: This ? mess is due to load entities race (we definitely need to wait until all entities are loaded)
                    controllers?.find((c) => c?.name === val?.name)?.name
                )
                .join(', ')
            }
            MenuProps={MenuProps}
          >
            {controllers?.map((controller) => (
              <MenuItem
                key={controller.id}
                value={controller}
                sx={{
                  gap: 2,
                }}
              >
                <Checkbox
                  checked={domainController?.some(
                    (c) => c?.id === controller.id
                  )}
                />
                <ListItemText primary={controller.name} />
              </MenuItem>
            ))}
          </MultiSelect>
          {errors.controller_ids ? (
            <Typography variant="body2" color="#e08d8d" sx={{ mb: '-20px' }}>
              {errors.controller_ids}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={4}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isLocalDomainsFormValid}
            size="large"
            onClick={createLocalDomainHandler}
            sx={{
              width: '100%',
              '&.Mui-disabled': {
                backgroundColor: 'primary.light',
                color: '#fff',
              },
            }}
          >
            Create Local Domain
          </Button>
        </Grid>
      </Grid>
      {errors.non_field_errors ? (
        <Typography variant="body2" color="#e08d8d">
          {errors.non_field_errors}
        </Typography>
      ) : null}
    </>
  );
};

export default LocalDomainsForm;
